import type { LogView } from '@aj/core';
import { Button, Card, Chip, HStack, Text, VStack } from '@aj/ui';
import { useState } from 'react';

import { LogForm } from '../../components/LogForm.js';
import { useI18n, useT } from '../../providers/I18nProvider.js';

export type LogCardProps = {
  log: LogView;
  className?: string;
  padding?: 4 | 5;
};

/**
 * Today's evidence — the other half of the daily loop.
 *
 * The tasks card beside it holds promises; this one holds what actually
 * happened. Recording progress is the intervention rather than a report on it,
 * and the effect is larger when the record is *visible* — so the day's total
 * is the biggest thing here, and the individual entries are underneath it as
 * proof rather than as a list to manage.
 *
 * Day-scoped, like every other card on Home. The whole ledger is Khata's job.
 */
export function LogCard({ log, className, padding = 4 }: LogCardProps) {
  const t = useT();
  const i18n = useI18n();
  const [logging, setLogging] = useState(false);

  return (
    <Card padding={padding} className={className}>
      <VStack gap={3}>
        <HStack gap={3} align="baseline" justify="between">
          <Text variant="overline" tone="subtle">
            {t('log.today')}
          </Text>
          {log.minutes > 0 && (
            <Text variant="label" tone="accent" numeric>
              {i18n.formatDuration(log.minutes)}
            </Text>
          )}
        </HStack>

        {log.tracks.length > 0 && (
          <HStack gap={2} className="flex-wrap">
            {log.tracks.map(({ track, minutes }) => (
              <Chip key={track} tone="neutral">
                {t('log.entry', { subject: track, minutes: i18n.formatDuration(minutes) })}
              </Chip>
            ))}
          </HStack>
        )}

        {log.entries.length === 0 ? (
          <Text variant="caption" tone="subtle">
            {t('log.nothingYet')}
          </Text>
        ) : (
          <VStack as="ul" gap={1} aria-label={t('log.today')}>
            {log.entries.map((entry) => (
              <HStack as="li" key={entry.id} gap={2} align="baseline">
                <Text variant="body" tone="ink" className="min-w-0 flex-1 truncate">
                  {entry.track}
                </Text>
                {/* Timed and review are stated in words, never by colour
                    alone — and both are facts about the entry a student would
                    otherwise have to remember. */}
                {entry.kind === 'review' && (
                  <Text variant="caption" tone="subtle" className="shrink-0">
                    {t('log.review')}
                  </Text>
                )}
                {entry.timed && (
                  <Text variant="caption" tone="subtle" className="shrink-0">
                    {t('log.timed')}
                  </Text>
                )}
                <Text variant="caption" tone="muted" numeric className="shrink-0">
                  {i18n.formatDuration(entry.minutes)}
                </Text>
              </HStack>
            ))}
            {log.hiddenCount > 0 && (
              <HStack as="li">
                <Text variant="caption" tone="subtle">
                  {t('log.moreEntries', { count: log.hiddenCount })}
                </Text>
              </HStack>
            )}
          </VStack>
        )}

        {logging ? (
          <LogForm onDone={() => setLogging(false)} />
        ) : (
          <Button
            variant="secondary"
            icon="plus"
            onClick={() => setLogging(true)}
            className="self-start"
          >
            {t('now.logEntry')}
          </Button>
        )}
      </VStack>
    </Card>
  );
}
